import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import parse from 'html-react-parser';
import * as actions from './redux/actions';
import voca from 'voca';
import TooltipInfo from '../../common/docs/TooltipInfo';
import { drapcodeIcon } from '../../common/custom/icon-library';
import { description } from '../../common/utils/event_constants';
import { checkPluginPermissions } from './utils';
import { pluginCode } from 'drapcode-constant';
import { isProjectReadOnly } from '../../common/utils/api_utils';

export class PluginDetails extends Component {
  static propTypes = {
    actions: PropTypes.object.isRequired,
    plugin: PropTypes.object.isRequired,
    projectId: PropTypes.string.isRequired,
  };

  synthesisCardLogoName = () => {
    const { currentPlugin } = this.props;
    if (currentPlugin.code === pluginCode.STRIPE_PAYMENT_METHODS) return 'stripe-logo.png';
    if ([pluginCode.AWS_SIGNATURE, pluginCode.AWS_SNS].includes(currentPlugin.code))
      return 'amazon-ses-logo.png';
    if (currentPlugin.code === pluginCode.CHATGPT_CHATBOT_MESSAGING) return 'chatgpt-logo.png';
    if (currentPlugin.code === pluginCode.SIGNZY_DIGITAL_DOCUMENT_SIGNING)
      return 'signzy-account-aggregator-logo.png';
    if (
      [
        pluginCode.IPRS_KENYA,
        pluginCode.METROPOL_CREDIT_REPORTS_BUREAU_KENYA,
        pluginCode.KRA_PIN_CHECKER,
        pluginCode.E_STATEMENT_ANALYSIS,
      ].includes(currentPlugin.code)
    ) {
      return 'spin-mobile-logo.png';
    }
    return currentPlugin.name.toLowerCase().replace(/[\s/]/g, '-') + '-logo.png';
  };

  showUninstallBtn = (selectedExtension, isReadOnly) => {
    const { currentPlugin, handleDelete, flag } = this.props;
    return (
      <button
        type="button"
        className="btn btn-sm btn-theme-delete"
        onClick={() => (flag && !isReadOnly ? handleDelete(currentPlugin.uuid) : undefined)}
        title={isReadOnly ? 'Read-Only Mode' : `Uninstall ${selectedExtension.name}`}
        style={{ cursor: flag && !isReadOnly ? 'pointer' : 'not-allowed' }}
        disabled={isReadOnly}
      >
        <i className="wb webconnect-trash-solid" /> Uninstall
        {flag ? null : (
          <>
            &nbsp;
            <TooltipInfo
              title={description}
              headerText={`Permission Disabled!`}
              showHeader={true}
              showArrow={true}
            />
          </>
        )}
      </button>
    );
  };

  showActionButtons = () => {
    const {
      currentPlugin,
      projectId,
      handleDelete,
      showInstalledPlugin,
      plugin,
      version,
      checkPermission,
    } = this.props;
    const isReadOnly = isProjectReadOnly(projectId);
    if (showInstalledPlugin) {
      const { extensions } = this.props;
      const selectedExtension = extensions.find((e) => e.code === currentPlugin.code);
      return (
        <>
          {selectedExtension.hasVariables ? (
            <>
              <Link
                to={`/plugin/${version}/v/${projectId}/update/${selectedExtension.uuid}/${currentPlugin.uuid}`}
                className="btn btn-sm btn-theme-submit"
                title={`Edit ${selectedExtension.name}`}
              >
                <i className="wb webconnect-pencil" /> Configure
              </Link>
              {this.showUninstallBtn(selectedExtension, isReadOnly)}
            </>
          ) : (
            <>
              <div className="offset-sm-4"></div>
              {this.showUninstallBtn(selectedExtension, isReadOnly)}
            </>
          )}
        </>
      );
    } else {
      //Check permission condition
      const { dependency: currentPluginDependency, code } = currentPlugin;
      const isAllowed = checkPluginPermissions(code);
      if (!isAllowed) {
        return (
          <div className="d-flex align-items-center">
            To use this plugin please &nbsp;
            <a
              href={`${process.env.REACT_APP_ORGANIZATION}accounts`}
              className="btn btn-sm btn-theme-submit"
            >
              <i className="wb webconnect-paper-plane-fill"></i> upgrade
            </a>
          </div>
        );
      }
      const { installedPlugins } = plugin;
      if (currentPluginDependency && currentPluginDependency.length > 0) {
        const dependentPluginExist = installedPlugins.find((plugin) =>
          currentPluginDependency.includes(plugin.code),
        );
        if (dependentPluginExist) {
          return this.installPluginBtn(projectId, currentPlugin, isReadOnly);
        } else {
          return this.disabledInstallBtn(currentPlugin);
        }
      } else {
        return this.installPluginBtn(projectId, currentPlugin, isReadOnly);
      }
    }
  };

  installPluginBtn = (projectId, currentPlugin, isReadOnly) => {
    const { version } = this.props;
    return (
      <div
        className="flex-space-evenly d-flex"
        style={{ cursor: isReadOnly ? 'not-allowed' : 'pointer' }}
      >
        <Link
          to={isReadOnly ? '#' : `/plugin/${version}/v/${projectId}/install/${currentPlugin.uuid}`}
          className={`btn btn-sm btn-theme-submit ${isReadOnly ? 'disabled' : ''}`}
          title={isReadOnly ? 'Read-Only Mode' : `Install ${currentPlugin.name}`}
          onClick={(e) => {
            if (isReadOnly) {
              e.preventDefault();
            }
          }}
        >
          <i className="wb webconnect-download"></i> Install
        </Link>
      </div>
    );
  };

  disabledInstallBtn = (plugin, planInfo = false) => {
    return (
      <>
        <div className="flex-space-evenly d-flex ">
          <TooltipInfo
            title={
              planInfo
                ? `Upgrade your plan to install ${plugin.name} plugin.`
                : `${plugin.dependencyMessage || 'Required plugin is not installed.'}`
            }
            headerText={planInfo ? 'Upgrade Plan' : 'Missing required plugin!'}
            showHeader={true}
            showArrow={true}
          >
            <span
              className="btn btn-sm disabled"
              style={{
                cursor: 'default',
                color: planInfo ? '#fff' : '#ffc857',
              }}
              disabled
            >
              <i className="wb webconnect-download"></i> Install{' '}
              <span
                style={{
                  cursor: 'default',
                  color: planInfo ? '#fff' : '#ffc857',
                }}
              >
                <i className={`wb ${drapcodeIcon(planInfo ? 'description' : 'warning')}`}></i>
              </span>
            </span>
          </TooltipInfo>
        </div>
      </>
    );
  };

  render() {
    const { currentPlugin, extensions } = this.props;
    if (!currentPlugin) {
      return '';
    }
    const selectedExtension = extensions.find((e) => e.code === currentPlugin.code);
    if (!selectedExtension) {
      return '';
    }
    let pluginLogo = '';
    try {
      pluginLogo = require('../../common/plugin/img/' + this.synthesisCardLogoName());
    } catch (error) {
      pluginLogo = require('../../common/plugin/img/drapcode-icon-192x192.png'); // Fallback Plugin Icon Image
    }

    return (
      <div className="card card--user-profile" key={currentPlugin.name}>
        <div className="card--user-profile__cover"></div>
        <div className="card-body">
          <figure className="avatar avatar--online">
            <div className="avatar__thumbnail">
              <img src={pluginLogo} alt={this.synthesisCardLogoName()} className="avatar__image" />
            </div>
            <figcaption className="avatar__meta">
              <h5 className="avatar__title js-user-name" title={currentPlugin.name}>
                {voca.truncate(currentPlugin.name, 35)}
              </h5>
              {selectedExtension.description && (
                <div
                  className="avatar__summery js-multiline-truncate"
                  title={selectedExtension.description}
                >
                  {parse(voca.truncate(selectedExtension.description, 135))}
                </div>
              )}
            </figcaption>
          </figure>
        </div>
        <div className="card-footer m-0 pl-3 pr-3 d-flex flex-space-between">
          {this.showActionButtons()}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    plugin: state.plugin,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(PluginDetails);
